import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { getSystemDocumentBqId } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { KnowledgeDocumentEntry } from 'types'

import { compareRevisionCodes } from './revisionHelpers'

/** Page to land on when no page-level metadata is available. */
export const DEFAULT_TARGET_PAGE = 1

/**
 * Default reference resolution — string match on title/drawing_number;
 * existing sites rely on this staying the default.
 */
export enum ReferenceResolverStrategy {
  LegacyStringMatch = 'legacy-string-match',
  IdBased = 'id-based',
}

export type NavigationTargetCandidate = {
  documentId: string
  pageNumber: number
  revisionCode?: string
}

/**
 * Earliest knowledge-document page for a unit tag reference, or page 1.
 */
export function resolveKnowledgeDocumentTargetPage(
  knowledgeDocuments: KnowledgeDocumentEntry[],
  systemDocumentEntryId: string
): number {
  const pages = knowledgeDocuments
    .filter(
      (doc) => doc.entry.tags['system-document'] === systemDocumentEntryId
    )
    .map((doc) => Number(doc.entry.data.fields.page_number))
    .filter((page) => Number.isFinite(page) && page >= 1)

  return pages.length > 0 ? Math.min(...pages) : DEFAULT_TARGET_PAGE
}

export type OffPageConnectorResolverInput = {
  strategy?: ReferenceResolverStrategy
  offPageConnectorDocuments: SystemDocumentEntry[]
  docReferenceTargets?: NavigationTargetCandidate[]
}

/** Resolve candidate targets for an OPC / drawing-number reference per the strategy. */
export function resolveOffPageConnectorTargets({
  strategy = ReferenceResolverStrategy.LegacyStringMatch,
  offPageConnectorDocuments,
  docReferenceTargets = [],
}: OffPageConnectorResolverInput): NavigationTargetCandidate[] {
  if (strategy === ReferenceResolverStrategy.IdBased) {
    return docReferenceTargets
  }

  return offPageConnectorDocuments.flatMap((document) => {
    const documentId = getSystemDocumentBqId(document)
    if (!documentId) return []
    return [
      {
        documentId,
        // String match is document-level only.
        pageNumber: DEFAULT_TARGET_PAGE,
        revisionCode: document.entry.data.revision_code,
      },
    ]
  })
}

export function allCandidatesHaveRevisionCodes(
  candidates: NavigationTargetCandidate[]
): boolean {
  return candidates.every(
    (candidate) =>
      candidate.revisionCode !== undefined &&
      candidate.revisionCode !== null &&
      candidate.revisionCode !== ''
  )
}

export function getLatestCandidateByRevision(
  candidates: NavigationTargetCandidate[]
): NavigationTargetCandidate | undefined {
  if (candidates.length === 0) return undefined
  const sortedByRevision = [...candidates].sort(
    (a, b) => -compareRevisionCodes(a.revisionCode ?? '', b.revisionCode ?? '')
  )
  return sortedByRevision[0]
}
